import type { Requirement } from "./requirements.ts";

/**
 * The industry layer (docs/self-improvement.md#five-layers): what any homeowners or landlord quote
 * needs whatever the carrier and the agency, and where a client's loan holds it. Owned by
 * Roundblock, the same for every agency. A carrier's own questions and defaults are in
 * `carriers/`; an agency's defaults are in `agency/`.
 */

// Where a client's mortgage loan holds a value (the crm-pull skill), read before it is asked.
export const BORROWER_INFO = "the loan's Borrower Info";
export const CURRENT_POLICY =
  "the current policy in the loan's documents (declarations page, evidence of insurance or invoice)";
export const DRIVER_LICENCE = "the borrower's driver's licence in the loan's documents";
export const MORTGAGE =
  "the current policy's mortgagee, the mortgage on this property among the loan's liabilities (only that one), the payoff demand or the Closing Disclosure's escrow";

// Requirements shared by every carrier, in the order the producer is asked.

export const firstName: Requirement = {
  key: "firstName",
  label: "First name",
  from: "client",
  need: "provide",
  verifyWith: "producer",
  ask: "The insured's first name",
};
export const lastName: Requirement = {
  key: "lastName",
  label: "Last name",
  from: "client",
  need: "provide",
  verifyWith: "producer",
  ask: "The insured's last name",
};
export const businessName: Requirement = {
  key: "businessName",
  label: "Business name",
  from: "client",
  need: "provide",
  ask: "The business's legal name",
};
export const dateOfBirth: Requirement = {
  key: "dateOfBirth",
  label: "Date of birth",
  from: "client",
  need: "provide",
  verifyWith: "producer",
  ask: "The insured's date of birth",
  lookIn: `${BORROWER_INFO}, or ${DRIVER_LICENCE}`,
};
export const phones: Requirement = {
  key: "phones",
  label: "Phone",
  from: "client",
  need: "provide",
  verifyWith: "producer",
  ask: "The insured's phone number",
  lookIn: BORROWER_INFO,
};
export const propertyAddress: Requirement = {
  key: "address",
  label: "Property address",
  from: "property",
  need: "provide",
  verifyWith: "producer",
  ask: "The property's street, city, state and ZIP",
  lookIn: "the loan's subject property",
};
export const effectiveDate: Requirement = {
  key: "effectiveDate",
  label: "Effective date",
  from: "quote",
  need: "provide",
  future: true,
  verifyWith: "choice",
  ask: "The date the policy should start",
};
/**
 * The current policy's premium. No carrier asks it: the producer compares ours with it, so it is
 * kept, with its source, like any value the quote is priced on.
 */
export const priorPremium: Requirement = {
  key: "priorPremium",
  label: "Current premium",
  from: "property",
  need: "confirm",
  compare: true,
  verifyWith: "producer",
  ask: "The current policy's annual premium, to compare with ours",
  lookIn: CURRENT_POLICY,
};
export const premiumPayer: Requirement = {
  key: "premiumPayer",
  label: "Who pays the premium",
  from: "quote",
  need: "provide",
  verifyWith: "producer",
  ask: "Who pays the premium: the insured, or the mortgagee (escrow)?",
  lookIn: `${MORTGAGE}; any mortgage means the mortgagee pays (escrow)`,
};
export const mortgagee: Requirement = {
  key: "mortgagee",
  label: "Mortgagee",
  from: "property",
  need: "provide",
  when: { key: "premiumPayer", equals: "mortgagee" },
  verifyWith: "producer",
  ask: "The mortgage lender's name and the loan number",
  lookIn: MORTGAGE,
};
/**
 * The current policy's other coverages and endorsements, with their limits (water or sewer backup,
 * other structures, personal property, ordinance or law…): a quote carries the same cover the
 * client has now (CGM feedback 2), so each is matched in the portal, or said to be missing there.
 */
export const priorCoverages: Requirement = {
  key: "priorCoverages",
  label: "The current policy's other coverages",
  from: "answer",
  need: "confirm",
  mirror: true,
  verifyWith: "producer",
  ask: "Every other coverage and endorsement on the current policy, with its limit (such as water or sewer backup), to match in the portal",
  lookIn: `${CURRENT_POLICY}: every coverage, limit and endorsement`,
};
/**
 * Facts the carriers fill in from public records. Ours are looked up first, and entered where the
 * carrier's differ (docs/decisions.md, 2026-10-26); the producer checks them.
 */
export const prefilled = (
  ...keys: ("yearBuilt" | "construction" | "roofType" | "squareFeet" | "stories" | "units")[]
): Requirement[] =>
  keys.map((key) => ({
    key,
    label: PREFILLED_LABELS[key],
    from: "property",
    need: "confirm",
    ask:
      key === "stories"
        ? "The stories above ground, 1 or more (a basement is never a story), if the carrier's value looks wrong"
        : `The ${PREFILLED_LABELS[key].toLowerCase()}, if the carrier's value looks wrong`,
    lookIn: PROPERTY_RECORDS_LOOK_IN,
  }));
/** Where a property fact is looked up, in order (the property-lookup skill). */
export const PROPERTY_RECORDS_LOOK_IN =
  "the appraisal in the loan's documents, then the property lookup: the county record, the county's website, then Zillow";
const PREFILLED_LABELS = {
  yearBuilt: "Year built",
  construction: "Construction",
  roofType: "Roof type",
  squareFeet: "Living area (sq ft)",
  stories: "Stories",
  units: "Units",
} as const;
